import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Checkbox, FormControlLabel, Link, Stack, TextField, Typography } from "@mui/material";
import { ApiError, get, post } from "@/api/client";
import AuthLayout from "@/brand/AuthLayout";
import CopyButton from "@/components/CopyButton";

interface Preview {
  valid: boolean;
  reason?: string;
  product?: { id: string; name: string; tagline?: string };
  plan?: { id: string; label: string; blurb?: string; pages_per_month?: number; users?: number };
  days?: number;
  partner?: string;
  scanner_model?: string;
}

interface Redeemed {
  trial_id: string;
  status: string;
  plan: string;
  expires_at?: string;
  login_url?: string;
  credentials: { admin_user: string; temp_password: string; login_url?: string };
}

/**
 * The page on the card in the scanner box.
 *
 * Public. The customer types the voucher code (or arrives with ?code=…),
 * sees what it gives and from whom, fills in who they are, and gets an
 * account on the spot. The password is shown here once and nowhere else.
 */
export default function RedeemPage() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") ?? "");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [checking, setChecking] = useState(false);
  const [company, setCompany] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [serial, setSerial] = useState("");
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Redeemed | null>(null);

  const normalised = code.trim().toUpperCase();

  useEffect(() => {
    if (normalised.length < 12) { setPreview(null); return; }
    let cancelled = false;
    setChecking(true);
    get<Preview>(`/public/trials/vouchers/${encodeURIComponent(normalised)}`)
      .then((result) => { if (!cancelled) setPreview(result); })
      .catch((err) => {
        if (!cancelled) setPreview({ valid: false, reason: err instanceof ApiError ? err.message : "Could not check that code" });
      })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [normalised]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await post<Redeemed>("/public/trials/redeem", {
        code: normalised,
        customer: { company: company.trim(), contact_name: contactName.trim(),
                    email: email.trim(), phone: phone.trim() },
        scanner_serial: serial.trim(),
        accept_terms: terms,
      });
      setDone(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    const url = done.credentials.login_url || done.login_url || "";
    return (
      <AuthLayout>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={600}>Your trial is ready</Typography>
          <Typography variant="body2" color="text.secondary">
            The {done.plan} plan is active{done.expires_at
              ? ` until ${new Date(done.expires_at).toLocaleDateString()}` : ""}. Write the
            password down now: it is shown once, and it is not in the email.
          </Typography>
          <Box sx={{ p: 2, borderRadius: 1, bgcolor: "action.hover", color: "text.primary" }}>
            <Detail label="Sign in" value={url} link />
            <Detail label="Username" value={done.credentials.admin_user} />
            <Detail label="Password" value={done.credentials.temp_password} mono />
          </Box>
          {url ? (
            <Button variant="contained" size="large" href={url} target="_blank" rel="noopener noreferrer">
              Open {preview?.product?.name ?? "the trial"}
            </Button>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            Reference {done.trial_id}. Your reseller can extend or reset the trial for you.
          </Typography>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Stack component="form" onSubmit={submit} spacing={2}>
        <div>
          <Typography variant="h5" fontWeight={600}>Redeem your scanner voucher</Typography>
          <Typography variant="body2" color="text.secondary">
            The code is on the card in the box. It starts a free cloud scanning trial.
          </Typography>
        </div>

        <TextField
          label="Voucher code" required value={code} autoFocus
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="MI-XXXX-XXXX"
          slotProps={{ htmlInput: { style: { fontFamily: "var(--md-font-mono)", letterSpacing: 1 } } }}
          helperText={checking ? "Checking…" : preview && !preview.valid ? preview.reason : " "}
          error={Boolean(preview && !preview.valid)}
        />

        {preview?.valid ? (
          <Alert severity="success">
            <strong>{preview.product?.name}</strong> — {preview.plan?.label} plan,{" "}
            {preview.days} days, from {preview.partner}
            {preview.scanner_model ? ` with your ${preview.scanner_model}` : ""}.
            {preview.plan?.blurb ? ` ${preview.plan.blurb}` : ""}
          </Alert>
        ) : null}

        {error && <Alert severity="error">{error}</Alert>}

        <TextField label="Company" required value={company}
                   onChange={(e) => setCompany(e.target.value)} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField label="Your name" value={contactName} sx={{ flex: 1 }}
                     onChange={(e) => setContactName(e.target.value)} autoComplete="name" />
          <TextField label="Work email" type="email" required value={email} sx={{ flex: 1 }}
                     onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                     helperText="Becomes your username" />
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField label="Phone" value={phone} sx={{ flex: 1 }}
                     onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
          <TextField label="Scanner serial number" value={serial} sx={{ flex: 1 }}
                     onChange={(e) => setSerial(e.target.value)}
                     helperText="On the label underneath the scanner" />
        </Stack>
        <FormControlLabel
          control={<Checkbox checked={terms} onChange={(e) => setTerms(e.target.checked)} />}
          label={<Typography variant="body2">I accept the trial terms and the privacy notice.</Typography>}
        />

        <Button type="submit" variant="contained" size="large"
                disabled={busy || !preview?.valid || !company.trim() || !email.trim() || !terms}>
          {busy ? "Creating your account…" : "Start my trial"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Already a partner? <Link href="/login">Sign in</Link>
        </Typography>
      </Stack>
    </AuthLayout>
  );
}

function Detail({ label, value, mono = false, link = false }: {
  label: string; value: string; mono?: boolean; link?: boolean;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 80, flexShrink: 0 }} fontWeight={600}>
        {label}
      </Typography>
      {link ? (
        <Link href={value} target="_blank" rel="noopener noreferrer" noWrap sx={{ minWidth: 0 }}>
          {value}
        </Link>
      ) : (
        <Typography variant="body2" component={mono ? "code" : "span"} noWrap sx={{ minWidth: 0 }}>
          {value}
        </Typography>
      )}
      {value ? <CopyButton value={value} /> : null}
    </Stack>
  );
}
